import React from 'react'
import { Icon } from './icons'
import { DataState } from './shared'
import { isApiError } from './api'
import type { ApiError, PeopleState, PersonEntry, PersonKind, Role } from './types'

const TABS: { key: PersonKind; label: string }[] = [
  { key: 'whitelist', label: 'Whitelist' },
  { key: 'ops', label: 'Ops' },
  { key: 'bans', label: 'Bans' },
]

interface PeoplePageProps {
  serverId: string
  serverName: string
  role: Role
  onFetch: () => Promise<PeopleState>
  onMutate: (
    kind: PersonKind,
    action: 'add' | 'remove',
    body: { name: string; reason?: string | null },
    verb: string,
  ) => Promise<boolean>
}

export function PeoplePage({ serverId, serverName, role, onFetch, onMutate }: PeoplePageProps) {
  const [active, setActive] = React.useState<PersonKind>('whitelist')
  const [state, setState] = React.useState<PeopleState | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<ApiError | null>(null)
  const [addOpen, setAddOpen] = React.useState(false)
  const [newName, setNewName] = React.useState('')
  const [newReason, setNewReason] = React.useState('')

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      const data = await onFetch()
      setState(data)
      setError(null)
    } catch (err) {
      if (isApiError(err)) setError(err)
      else setError({ status: 0, message: 'Network error' })
    } finally {
      setLoading(false)
    }
  }, [onFetch])

  React.useEffect(() => { void reload() }, [reload, serverId])

  const canAct = role === 'owner' || role === 'admin'

  async function handleAdd() {
    const name = newName.trim()
    if (!name) return
    const body = active === 'bans' ? { name, reason: newReason.trim() || null } : { name }
    const ok = await onMutate(active, 'add', body, active === 'bans' ? 'Banned' : 'Added')
    if (ok) {
      setAddOpen(false)
      setNewName('')
      setNewReason('')
      void reload()
    }
  }

  async function handleRemove(entry: PersonEntry) {
    const ok = await onMutate(active, 'remove', { name: entry.name }, 'Removed')
    if (ok) void reload()
  }

  const entries = state ? state[active] : []

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="seg-radio">
          {TABS.map(tab => (
            <button key={tab.key} className={active === tab.key ? 'active' : ''} onClick={() => { setActive(tab.key); setAddOpen(false) }}>
              {tab.label}
              {state && <span className="dim" style={{ marginLeft: 6 }}>{state[tab.key].length}</span>}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button className="btn sm ghost" onClick={() => void reload()}><Icon name="rotate-ccw" size={11} /> Refresh</button>
          {canAct && (
            <button className="btn sm primary" onClick={() => setAddOpen(open => !open)}>
              <Icon name="plus" size={11} /> {active === 'bans' ? 'Ban' : 'Add'} player
            </button>
          )}
        </div>
      </div>

      {addOpen && canAct && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div className="flex gap-2 items-end" style={{ flexWrap: 'wrap' }}>
            <label style={{ display: 'block', flex: '1 1 180px' }}>
              <div className="text-xs dim mb-1">Minecraft username</div>
              <input
                type="text"
                value={newName}
                onChange={event => setNewName(event.target.value)}
                placeholder="e.g. KaiC_5504"
                maxLength={16}
                pattern="[A-Za-z0-9_]{1,16}"
                style={{ width: '100%' }}
              />
            </label>
            {active === 'bans' && (
              <label style={{ display: 'block', flex: '1 1 240px' }}>
                <div className="text-xs dim mb-1">Reason (optional)</div>
                <input
                  type="text"
                  value={newReason}
                  onChange={event => setNewReason(event.target.value)}
                  placeholder="e.g. grief"
                  maxLength={200}
                  style={{ width: '100%' }}
                />
              </label>
            )}
            <div className="flex gap-2">
              <button className="btn ghost sm" onClick={() => { setAddOpen(false); setNewName(''); setNewReason('') }}>Cancel</button>
              <button className="btn primary sm" onClick={() => void handleAdd()} disabled={!newName.trim()}>
                <Icon name="check" size={11} /> Confirm
              </button>
            </div>
          </div>
          <div className="text-xs dim" style={{ marginTop: 6 }}>Writes via RCON on {serverName}. Must match Minecraft username regex (letters, digits, underscore, max 16).</div>
        </div>
      )}

      <DataState loading={loading} error={error} onRetry={reload}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {entries.length === 0 ? (
            <div className="dim text-sm" style={{ padding: 28, textAlign: 'center' }}>
              {active === 'whitelist' && 'No players whitelisted.'}
              {active === 'ops' && 'No operators.'}
              {active === 'bans' && 'No banned players.'}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '34%' }}>Name</th>
                    <th>UUID</th>
                    {active === 'ops' && <th style={{ width: 80 }}>Level</th>}
                    {active === 'bans' && <th>Reason</th>}
                    {canAct && <th style={{ width: 90 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={`${active}-${entry.name}`}>
                      <td className="mono" style={{ fontWeight: 500 }}>{entry.name}</td>
                      <td className="mono text-xs dim">{entry.uuid ?? '—'}</td>
                      {active === 'ops' && <td className="mono text-xs">{entry.level ?? '—'}</td>}
                      {active === 'bans' && <td className="text-xs" style={{ opacity: 0.85 }}>{entry.reason ?? ''}</td>}
                      {canAct && (
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn sm ghost" onClick={() => void handleRemove(entry)} style={{ color: 'var(--rose-400)' }}>
                            <Icon name="x" size={11} /> Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DataState>
    </div>
  )
}
